<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import BusinessDay from './BusinessDay.vue';
import HolidaySettingRow from './HolidaySettingRow.vue';
import {
  timeSlotParse,
  timeSlotTransform,
  holidaySettingsParse,
  holidaySettingsTransform,
  createDefaultHolidaySetting,
  defaultTimeSlot,
  timeZoneOptions,
} from '../helpers/businessHour';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import {
  buildAutoReplyTemplateOption,
  buildAutoReplyTemplateOptions,
  findAutoReplyTemplateByConfig,
  findAutoReplyTemplateByKey,
  getAutoReplyTemplateKey,
  getAutoReplyTemplatePreview,
  serializeAutoReplyTemplateConfig,
} from '../helpers/autoReplyTemplates';

const DEFAULT_TIMEZONE = {
  label: 'Pacific Time (US & Canada) (GMT-07:00)',
  value: 'America/Los_Angeles',
};

export default {
  components: {
    SettingsToggleSection,
    SettingsFieldSection,
    BusinessDay,
    HolidaySettingRow,
    NextButton,
    WootMessageEditor,
    ComboBox,
    SelectInput,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isBusinessHoursEnabled: false,
      unavailableMessage: '',
      unavailableMessageMode: 'message',
      outOfOfficeTemplate: {},
      timeZone: DEFAULT_TIMEZONE,
      timeSlots: [...defaultTimeSlot],
      holidaySettings: [],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
      teams: 'teams/getTeams',
      agentBots: 'agentBots/getBots',
    }),
    dayNames() {
      const locale = (this.$i18n?.locale || 'en').replace('_', '-');

      try {
        const formatter = new Intl.DateTimeFormat(locale, {
          weekday: 'long',
          timeZone: 'UTC',
        });

        return Array.from({ length: 7 }, (_, day) => {
          const date = new Date(Date.UTC(2024, 0, 7 + day));
          return formatter.format(date);
        });
      } catch (error) {
        return [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ];
      }
    },
    hasError() {
      if (!this.isBusinessHoursEnabled) return false;
      const hasInvalidWeeklyHours =
        this.timeSlots.filter(slot => slot.from && !slot.valid).length > 0;
      const hasInvalidHolidays = this.holidaySettings.some(holiday =>
        this.isHolidayInvalid(holiday)
      );
      const hasMissingTemplateSelection =
        this.unavailableMessageMode === 'template' &&
        !this.outOfOfficeTemplateValue;

      return (
        hasInvalidWeeklyHours ||
        hasInvalidHolidays ||
        hasMissingTemplateSelection
      );
    },
    timeZones() {
      return [...timeZoneOptions()];
    },
    botOptions() {
      return [
        {
          label: this.$t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.NO_BOT'),
          value: '',
        },
        ...this.agentBots.map(bot => ({
          label: bot.name,
          value: bot.id,
        })),
      ];
    },
    teamOptions() {
      return [
        {
          label: this.$t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.NO_TEAM'),
          value: '',
        },
        ...this.teams.map(team => ({
          label: team.name,
          value: team.id,
        })),
      ];
    },
    timeZoneValue: {
      get() {
        return this.timeZone.value;
      },
      set(value) {
        const match = this.timeZones.find(tz => tz.value === value);
        if (match) this.timeZone = match;
      },
    },
    isRichEditorEnabled() {
      if (
        this.isATwilioChannel ||
        this.isATwitterInbox ||
        this.isAFacebookInbox
      )
        return false;
      return true;
    },
    isNativeWhatsAppAutoReplyInbox() {
      return this.channelType === 'Channel::Whatsapp';
    },
    currentOutOfOfficeTemplate() {
      return (
        findAutoReplyTemplateByConfig(
          this.inbox.message_templates || [],
          this.outOfOfficeTemplate
        ) ||
        (this.outOfOfficeTemplate?.name ? this.outOfOfficeTemplate : null)
      );
    },
    unavailableMessageModeOptions() {
      const canUseTemplateMode =
        this.outOfOfficeTemplateOptions.length > 0 ||
        !!this.outOfOfficeTemplateValue;

      return [
        {
          value: 'message',
          label: this.$t('INBOX_MGMT.AUTO_REPLY_TEMPLATE.MESSAGE_OPTION'),
        },
        {
          value: 'template',
          label: this.$t('INBOX_MGMT.AUTO_REPLY_TEMPLATE.TEMPLATE_OPTION'),
          disabled: !canUseTemplateMode,
        },
      ];
    },
    outOfOfficeTemplateOptions() {
      const options = buildAutoReplyTemplateOptions(
        this.inbox.message_templates || []
      );
      const currentValue = getAutoReplyTemplateKey(this.outOfOfficeTemplate);

      if (currentValue && !options.some(option => option.value === currentValue)) {
        return [
          buildAutoReplyTemplateOption(this.outOfOfficeTemplate),
          ...options,
        ];
      }

      return options;
    },
    outOfOfficeTemplateValue: {
      get() {
        return getAutoReplyTemplateKey(this.outOfOfficeTemplate);
      },
      set(value) {
        const selectedTemplate = findAutoReplyTemplateByKey(
          this.inbox.message_templates || [],
          value
        );

        this.outOfOfficeTemplate = serializeAutoReplyTemplateConfig(
          selectedTemplate || this.outOfOfficeTemplate
        );
      },
    },
    outOfOfficeTemplateMessage() {
      if (!this.outOfOfficeTemplateOptions.length) {
        return this.$t('INBOX_MGMT.AUTO_REPLY_TEMPLATE.EMPTY');
      }

      if (
        this.outOfOfficeTemplate?.name &&
        !findAutoReplyTemplateByConfig(
          this.inbox.message_templates || [],
          this.outOfOfficeTemplate
        )
      ) {
        return this.$t('INBOX_MGMT.AUTO_REPLY_TEMPLATE.CURRENT_UNAVAILABLE');
      }

      const preview = getAutoReplyTemplatePreview(
        this.currentOutOfOfficeTemplate
      );
      return preview || this.$t('INBOX_MGMT.AUTO_REPLY_TEMPLATE.HELP_TEXT');
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
    this.fetchHolidayDependencies();
  },
  methods: {
    fetchHolidayDependencies() {
      this.$store.dispatch('teams/get');
      this.$store.dispatch('agentBots/get');
    },
    setDefaults() {
      const {
        working_hours_enabled: isEnabled = false,
        out_of_office_message: unavailableMessage,
        working_hours: timeSlots = [],
        holiday_settings: holidaySettings = [],
        timezone: timeZone,
      } = this.inbox;
      const slots = timeSlotParse(timeSlots).length
        ? timeSlotParse(timeSlots)
        : defaultTimeSlot;
      this.isBusinessHoursEnabled = isEnabled;
      this.unavailableMessage = unavailableMessage || '';
      this.outOfOfficeTemplate = this.inbox.out_of_office_template || {};
      this.unavailableMessageMode =
        this.isNativeWhatsAppAutoReplyInbox && this.outOfOfficeTemplate?.name
          ? 'template'
          : 'message';
      this.timeSlots = slots;
      this.holidaySettings = holidaySettingsParse(holidaySettings);
      this.timeZone =
        this.timeZones.find(item => timeZone === item.value) ||
        DEFAULT_TIMEZONE;
    },
    onSlotUpdate(slotIndex, slotData) {
      this.timeSlots = this.timeSlots.map(item =>
        item.day === slotIndex ? slotData : item
      );
    },
    onHolidayUpdate(holidayIndex, holidayData) {
      this.holidaySettings = this.holidaySettings.map((holiday, index) =>
        index === holidayIndex ? holidayData : holiday
      );
    },
    handleUnavailableMessageModeChange(value) {
      this.unavailableMessageMode = value;

      if (value === 'message') {
        this.outOfOfficeTemplate = {};
      }
    },
    addHoliday() {
      this.holidaySettings = [
        ...this.holidaySettings,
        createDefaultHolidaySetting(),
      ];
    },
    removeHoliday(holidayIndex) {
      this.holidaySettings = this.holidaySettings.filter(
        (_, index) => index !== holidayIndex
      );
    },
    isHolidayInvalid(holiday) {
      return (
        !holiday.date ||
        !holiday.name?.trim() ||
        !holiday.from ||
        !holiday.to ||
        !holiday.valid
      );
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          working_hours_enabled: this.isBusinessHoursEnabled,
          out_of_office_message: this.unavailableMessage,
          out_of_office_template:
            this.unavailableMessageMode === 'template'
              ? this.outOfOfficeTemplate
              : null,
          working_hours: timeSlotTransform(this.timeSlots),
          holiday_settings: holidaySettingsTransform(this.holidaySettings),
          timezone: this.timeZone.value,
          channel: {},
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="mx-6">
    <SettingsToggleSection
      v-model="isBusinessHoursEnabled"
      :header="$t('INBOX_MGMT.BUSINESS_HOURS.TOGGLE_AVAILABILITY')"
      :description="$t('INBOX_MGMT.BUSINESS_HOURS.TOGGLE_HELP')"
    >
      <template v-if="isBusinessHoursEnabled" #editor>
        <div
          v-if="isNativeWhatsAppAutoReplyInbox"
          class="mb-4 flex flex-col gap-4"
        >
          <div class="flex flex-col gap-1.5">
            <label class="text-sm text-n-slate-12">
              {{ $t('INBOX_MGMT.AUTO_REPLY_TEMPLATE.MODE_LABEL') }}
            </label>
            <SelectInput
              :model-value="unavailableMessageMode"
              :options="unavailableMessageModeOptions"
              class="!w-full [&>select]:w-full"
              @update:model-value="handleUnavailableMessageModeChange"
            />
          </div>

          <template v-if="unavailableMessageMode === 'message'">
            <WootMessageEditor
              v-if="isRichEditorEnabled"
              v-model="unavailableMessage"
              enable-variables
              is-format-mode
              :placeholder="
                $t('INBOX_MGMT.BUSINESS_HOURS.UNAVAILABLE_MESSAGE_LABEL')
              "
              :min-height="4"
            />
            <textarea v-else v-model="unavailableMessage" type="text" />
          </template>

          <div v-else class="flex flex-col gap-1.5">
            <label class="text-sm text-n-slate-12">
              {{ $t('INBOX_MGMT.AUTO_REPLY_TEMPLATE.TEMPLATE_LABEL') }}
            </label>
            <ComboBox
              v-model="outOfOfficeTemplateValue"
              :options="outOfOfficeTemplateOptions"
              :placeholder="
                $t('INBOX_MGMT.AUTO_REPLY_TEMPLATE.TEMPLATE_PLACEHOLDER')
              "
              :search-placeholder="
                $t('INBOX_MGMT.AUTO_REPLY_TEMPLATE.SEARCH_PLACEHOLDER')
              "
              :empty-state="$t('INBOX_MGMT.AUTO_REPLY_TEMPLATE.EMPTY')"
              :message="outOfOfficeTemplateMessage"
              :has-error="
                unavailableMessageMode === 'template' && !outOfOfficeTemplateValue
              "
            />
          </div>
        </div>

        <div v-else class="mb-4">
          <WootMessageEditor
            v-if="isRichEditorEnabled"
            v-model="unavailableMessage"
            enable-variables
            is-format-mode
            :placeholder="
              $t('INBOX_MGMT.BUSINESS_HOURS.UNAVAILABLE_MESSAGE_LABEL')
            "
            :min-height="4"
          />
          <textarea v-else v-model="unavailableMessage" type="text" />
        </div>
      </template>
    </SettingsToggleSection>

    <div v-if="isBusinessHoursEnabled" class="flex items-center my-8 py-1">
      <div class="flex-1 h-px bg-n-weak" />
      <span class="text-body-main text-n-slate-11 px-2">
        {{ $t('INBOX_MGMT.BUSINESS_HOURS.WEEKLY_TITLE') }}
      </span>
      <div class="flex-1 h-px bg-n-weak" />
    </div>

    <SettingsFieldSection
      v-if="isBusinessHoursEnabled"
      :label="$t('INBOX_MGMT.BUSINESS_HOURS.TIMEZONE_LABEL')"
    >
      <ComboBox
        v-model="timeZoneValue"
        :options="timeZones"
        :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
        class="[&>div>button]:!bg-n-alpha-black2"
      />
    </SettingsFieldSection>

    <form class="flex flex-col" @submit.prevent="updateInbox">
      <div v-if="isBusinessHoursEnabled" class="mt-2">
        <div class="w-full">
          <table
            class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
          >
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  class="py-3 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-start text-heading-3 text-n-slate-12"
                >
                  {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.DAY') }}
                </th>
                <th
                  class="py-3 ltr:pr-3 rtl:pl-3 text-start text-heading-3 text-n-slate-12"
                >
                  {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.AVAILABILITY') }}
                </th>
                <th
                  class="py-3 ltr:pr-3 rtl:pl-3 text-start text-heading-3 text-n-slate-12"
                >
                  {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.HOURS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <BusinessDay
                v-for="timeSlot in timeSlots"
                :key="timeSlot.day"
                :day-name="dayNames[timeSlot.day]"
                :time-slot="timeSlot"
                @update="data => onSlotUpdate(timeSlot.day, data)"
              />
            </tbody>
          </table>
        </div>
      </div>

      <template v-if="isBusinessHoursEnabled">
        <div class="flex items-center my-8 py-1">
          <div class="flex-1 h-px bg-n-weak" />
          <span class="text-body-main text-n-slate-11 px-2">
            {{ $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.TITLE') }}
          </span>
          <div class="flex-1 h-px bg-n-weak" />
        </div>

        <div class="flex items-center justify-between gap-3 mb-4">
          <p class="mb-0 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.SUBTITLE') }}
          </p>
          <NextButton
            type="button"
            sm
            slate
            icon="i-lucide-plus"
            :label="$t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.ADD')"
            @click="addHoliday"
          />
        </div>

        <div class="w-full">
          <table
            class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
          >
            <tbody v-if="holidaySettings.length" class="divide-y divide-n-weak">
              <HolidaySettingRow
                v-for="(holiday, index) in holidaySettings"
                :key="`${holiday.date || 'holiday'}-${index}`"
                :holiday="holiday"
                :bot-options="botOptions"
                :team-options="teamOptions"
                @update="data => onHolidayUpdate(index, data)"
                @remove="removeHoliday(index)"
              />
            </tbody>
            <tbody v-else>
              <tr>
                <td class="p-4 text-sm text-n-slate-11">
                  {{ $t('INBOX_MGMT.BUSINESS_HOURS.HOLIDAYS.EMPTY') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <div class="w-full flex justify-end items-center py-4 mt-2">
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.BUSINESS_HOURS.UPDATE')"
          :is-loading="uiFlags.isUpdating"
          :disabled="hasError"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
:deep(.message-editor) {
  @apply border-0;
}

textarea {
  @apply min-h-[4rem] mt-1.5;
}
</style>
